import { useState } from "react";
import { Sparkles, Send, Eye, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { http, fmtDate, errMsg, tname } from "@/lib/api";

const Team = ({ t, align }) => (
  <div className={`flex items-center gap-3 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
    <img src={t.crest} alt={tname(t)} className="w-10 h-10 object-contain" />
    <span className="font-semibold text-base leading-tight">{tname(t)}</span>
  </div>
);

export function ProbBar({ p }) {
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-slate-800">
      <div style={{ width: `${p.prob_1}%`, background: "var(--win)" }} />
      <div style={{ width: `${p.prob_x}%`, background: "var(--draw)" }} />
      <div style={{ width: `${p.prob_2}%`, background: "var(--loss)" }} />
    </div>
  );
}

export default function MatchCard({ match: m, code, index, onOpen, onPredicted, readOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const p = m.prediction?.data;
  const finished = m.status === "FINISHED";
  const live = m.status === "IN_PLAY" || m.status === "PAUSED";
  const outcome = { HOME_TEAM: "1", DRAW: "X", AWAY_TEAM: "2" }[m.score?.winner];
  const hit = finished && p && outcome && p.esito.includes(outcome);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/predictions/${m.id}`, null, { params: { competition: code, force: !!p } });
      onPredicted(m.id, r.data);
      toast.success(`Pronostico generato: ${tname(m.homeTeam)} - ${tname(m.awayTeam)}`);
      onOpen();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await http.post(`/telegram/send/${m.id}`, null, { params: { competition: code } });
      toast.success(`Inviato a ${r.data.sent} iscritti Telegram`);
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  return (
    <div data-testid={`match-card-${index}`} className={`panel panel-hover p-5 fade-up ${finished ? "opacity-80" : ""}`} style={{ animationDelay: `${index * 40}ms` }}>
      <div className="flex justify-between text-xs font-mono text-slate-400 mb-4">
        <span>{fmtDate(m.utcDate)}</span>
        {finished ? (
          <span data-testid={`played-badge-${index}`} className="flex items-center gap-1 text-[var(--win)]"><CheckCircle2 size={14} /> GIÀ GIOCATA</span>
        ) : (
          <span className={live ? "text-[var(--loss)]" : "text-[var(--gold)]"}>{live ? "● LIVE" : "IN PROGRAMMA"}</span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <Team t={m.homeTeam} />
        <div className="font-display text-3xl font-extrabold text-center min-w-[64px]">
          {finished ? `${m.score.fullTime.home}-${m.score.fullTime.away}` : p ? <span className="text-[var(--gold)]">{p.esito}</span> : <span className="text-slate-600">VS</span>}
        </div>
        <Team t={m.awayTeam} align="right" />
      </div>
      {finished && p && (
        <div data-testid={`prediction-outcome-${index}`} className={`mt-4 text-xs font-semibold flex items-center gap-1 ${hit ? "text-[var(--win)]" : "text-[var(--loss)]"}`}>
          {hit ? <CheckCircle2 size={14} /> : <XCircle size={14} />} Pronostico {p.esito} {hit ? "azzeccato" : "non azzeccato"}
        </div>
      )}
      {p && (
        <div className="mt-4 space-y-2">
          <ProbBar p={p} />
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>1 {p.prob_1}% · X {p.prob_x}% · 2 {p.prob_2}%</span>
            <span>{p.over_under} · {p.goal_nogoal} · <b className="text-white">{p.confidenza}%</b></span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-5">
        {!finished && !readOnly && (
          <button data-testid={`generate-prediction-btn-${index}`} disabled={busy} onClick={generate} className={`btn btn-gold ${busy ? "scan" : ""}`}>
            <Sparkles size={16} /> {busy ? "Analisi AI in corso..." : p ? "Rigenera pronostico" : "Genera pronostico AI"}
          </button>
        )}
        {readOnly && !finished && !p && <span className="text-xs text-slate-500">Pronostico in arrivo</span>}
        {finished && !p && <span className="text-xs text-slate-500">Nessun pronostico generato prima della partita</span>}
        {p && (
          <>
            <button data-testid={`view-prediction-btn-${index}`} onClick={onOpen} className="btn btn-ghost"><Eye size={16} /> Dettagli</button>
            {!readOnly && <button data-testid={`send-telegram-btn-${index}`} disabled={sending} onClick={send} className="btn btn-tg"><Send size={16} /> {sending ? "Invio..." : "Invia agli iscritti"}</button>}
          </>
        )}
      </div>
    </div>
  );
}
